import { Tooltip, Typography } from "antd";
import { clsx } from "clsx";

import styles from "./RVLoading.module.scss";

export type RVLoadingProps = {
    /**
     * Optional text to display below the loading animation.
     * @default "Loading your content..."
     */
    text?: string | null;
    /**
     * Whether to render a smaller version of the loading animation.
     * When true, reduces the size of the loading indicator.
     * @default false
     */
    small?: boolean;
    /**
     * Whether to truncate the loading text with ellipsis if it overflows.
     * @default false
     */
    ellipsis?: boolean;
};

/**
 * An animated loading indicator with three bouncing bars.
 *
 * Displays an animated purple loading indicator with optional text below.
 * The animation consists of three vertical bars that alternate in height
 * to create a wave effect.
 *
 * @example
 * ```tsx
 * // Default loading with text
 * <RVLoading />
 *
 * // Custom loading text
 * <RVLoading text="Processing your request..." />
 *
 * // Small loading indicator without text
 * <RVLoading text={null} small />
 *
 * // Loading with truncated text
 * <RVLoading text="Very long loading message..." ellipsis />
 * ```
 */
export function RVLoading({
    text = "Loading your content...",
    small = false,
    ellipsis = false,
}: RVLoadingProps): JSX.Element {
    return (
        <div className={clsx(styles.wrapper, { [styles.small]: small || !text })}>
            <div className={styles.inner}>
                <div className={styles.first}></div>
                <div className={styles.second}></div>
                <div className={styles.third}></div>
            </div>
            {text && (
                <div
                    className={clsx(styles.loadingText, {
                        [styles.smallText]: small,
                        [styles.truncateText]: ellipsis,
                    })}
                >
                    <Tooltip title={text}>
                        <Typography.Text ellipsis={ellipsis}>{text}</Typography.Text>
                    </Tooltip>
                </div>
            )}
        </div>
    );
}
